// Angular imports
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';

// 3rd party imports
import { DxBoxComponent, DxResponsiveBoxComponent } from 'devextreme-angular';

// CC-Common imports
import { CCLocalStorageService, DxWidgetUtils } from 'cc-common';

// Component & Interface imports
import { MPBaseDashboardWidgetComponent } from '../../../_base/mp-base-dashboard-widget.component';
import { MultiColumnSelectorComponent } from '../../../management-common/component/common/multi-column-selector/multi-column-selector.component';
import { IInteractiveDashboardComponent } from '../../../dashboard-module/_interfaces/interactive-dashboard-component';

// Service imports
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { WidgetContextFactoryService } from '../../../_services/data-services/widget-context-factory.service';
import { UserService } from '../../../_services/data-services/user.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { SharedWidgetsService } from '../../_services/shared-widgets.service';

// Model imports
import { UserDashboardResultDto } from '../../_models/user-dashboard-result-dto';
import { UserDashboardProxyListResultDto } from '../../_models/user-dashboard-proxy-list-result-dto';
import { UserDashboardParametersDto } from '../../_models/user-dashboard-parameters-dto';

@Component({
    selector: 'app-user-dashboard-widget',
    templateUrl: './user-dashboard-widget.component.html',
    styleUrls: ['./user-dashboard-widget.component.scss']
})
export class UserDashboardWidgetComponent
    extends MPBaseDashboardWidgetComponent
    implements OnInit, AfterViewInit, IInteractiveDashboardComponent
{
    @ViewChild('barChartBox', { static: false }) barChartBox: DxBoxComponent;
    @ViewChild('userSelector', { static: false }) userSelector: MultiColumnSelectorComponent;
    @ViewChild('responsiveBox', { static: false }) responsiveBox: DxResponsiveBoxComponent;

    hideTitle = false;
    hideFullScreen = false;
    minWidth = 420;
    minHeight = 600;

    titleText: string;
    dashboardData: UserDashboardResultDto[];
    viewOptions: any[];
    selectedViewOption: any;

    chartTypes: string[] = ['Side by Side', 'Stacked'];
    selectedChartType = 'Side by Side';
    clearCache = false;

    userList: UserDashboardProxyListResultDto[];
    selectedUser: UserDashboardProxyListResultDto;
    userColumns: any[];
    userGridBoxValue: string[] = [];

    widgetWidth: number;

    constructor(
        protected readonly contextService: ContextManagerService,
        protected readonly widgetContextFactory: WidgetContextFactoryService,
        protected readonly localStorageService: CCLocalStorageService,
        private readonly userService: UserService,
        private readonly errorService: ServerErrorService,
        private readonly widgetService: SharedWidgetsService
    ) {
        super(contextService, widgetContextFactory, localStorageService);
        this.userColumns = this.getUserColumns();
        this.viewOptions = [
            { text: 'Show My Open Work', value: false },
            { text: 'Show All Open Work', value: true }
        ];
    }

    // Override
    ngOnInit(): void {
        super.ngOnInit();
        this.getUserList();
    }

    ngAfterViewInit(): void {
        // Workaround to listen to close event of dropdownBox of multi-selector component
        setTimeout(() => {
            if (DxWidgetUtils.hasInstance(this.userSelector)) {
                this.userSelector.dropdownBox.instance.on('closed', () => {
                    if (!this.userGridBoxValue.length) {
                        this.selectedUser = UserDashboardProxyListResultDto.transform(
                            this.userService.getCurrentUserInfo()
                        );
                        this.userGridBoxValue = [this.selectedUser.userId];
                    }
                });
            }
        }, 1000);
    }

    // Override
    refresh(width: number, height: number): void {
        this.chartWidth = width - 15;
        this.chartHeight = height - 75;

        this.loadingCompleted = false;
        setTimeout(() => {
            this.loadingCompleted = true;
        }, 500);

        this.widgetWidth = width;
        console.log(`width = ${width}`);
    }

    // Override
    getTitle(name: string): string {
        this.titleText = name;
        if (this.titleText === 'Team Open Work') {
            this.selectedViewOption = this.viewOptions[1];
        } else {
            this.selectedViewOption = this.viewOptions[0];
        }
        return name;
    }

    populateDashboardData(): void {
        if (!!this.selectedUser && !!this.titleText) {
            this.loadingVisible = true;
            const params = new UserDashboardParametersDto();
            params.userId = this.selectedUser.userId;
            params.includeAll = this.selectedViewOption.value;
            params.clearCache = this.clearCache;
            this.widgetService.getUserDashboard(params).subscribe(
                (results) => {
                    this.dashboardData = results;
                    this.loadingVisible = false;
                    this.clearCache = false;

                    this.loadingCompleted = false;
                    setTimeout(() => (this.loadingCompleted = true), 500);
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    onRefreshClick(): void {
        this.clearCache = true;
        this.populateDashboardData();
    }

    onViewOptionChange(): void {
        this.populateDashboardData();
    }

    onChartTypeChange(): void {
        this.loadingCompleted = false;
        setTimeout(() => (this.loadingCompleted = true), 500);
    }

    onUserSelection(selectedItem: any): void {
        if (!!selectedItem && selectedItem.selectedRow.length > 0) {
            const selectedRows: any[] = selectedItem.selectedRow;
            if (this.selectedUser.userId !== selectedRows[0].userId) {
                this.selectedUser = selectedRows[0];
                this.userGridBoxValue = [this.selectedUser.userId];
                this.populateDashboardData();
            }
        } else if (this.userGridBoxValue.length > 0) {
            this.selectedUser = UserDashboardProxyListResultDto.transform(this.userService.getCurrentUserInfo());
            this.userGridBoxValue = [];
            this.userSelector.resetSelector();
            this.populateDashboardData();
        }
    }

    private getUserList() {
        this.widgetService.getUserDashboardUsers().subscribe(
            (results) => {
                this.userList = results.filter((r, i, l) => l.findIndex((x) => x.userId === r.userId) === i);
                this.selectedUser = UserDashboardProxyListResultDto.transform(this.userService.getCurrentUserInfo());
                this.userGridBoxValue = [this.selectedUser.userId];
                this.populateDashboardData();
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getUserColumns(): any[] {
        return [
            {
                dataField: 'name',
                dataType: 'string',
                caption: 'Name'
            },
            {
                dataField: 'title',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'managementCompany',
                dataType: 'string',
                caption: 'Management Company'
            },
            {
                dataField: 'location',
                dataType: 'string',
                caption: 'Location'
            }
        ];
    }
}
